import { Injectable } from '@angular/core';
import { AuthResponse, AuthResponseUser, UserSchool, UserType } from '../models/user.model';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';
import { tap } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { AvailableActions } from 'bookraw/shared/models/permission.model';
import { isString } from 'micro-dash';
import { School } from 'bookraw/shared/models/school.model';
import { Partner } from 'bookraw/shared/models/partner.model';
import { UsersService } from 'bookraw/shared/services/users.service';
import * as moment from 'moment';
import { Type } from 'bookraw/shared/models/sso-setting.model';
import { SsoSettingsService } from 'bookraw/shared/services/sso-settings.service';

const bookrawAuthKey = 'bookraw_admin_auth';
const bookrawSelectedSchool = 'bookraw_selected_school';
const bookrawSelectedPartner = 'bookraw_selected_partner';
const bookrawLoggedUser = 'bookraw_logged_user';
const bookrawCountryCode = 'bookraw_country_code';

export type Scope = 'global' | 'partners' | 'schools' | 'ssoTeacher' | 'ssoPartner' | 'whiteLabelTeacher'

@Injectable()
export class UserService {

  public loginChanges = new BehaviorSubject<AuthResponseUser>(this.user);
  public schoolChange = new BehaviorSubject<School>(this.school);
  public partnerChange = new BehaviorSubject<Partner>(this.partner);
  public loggedUserChange = new BehaviorSubject<string>(this.loggedUser);
  public countryCodeChange = new BehaviorSubject<string>(this.countryCode);
  public userLogin = new Subject();

  private _auth: AuthResponse;
  private _school: UserSchool;
  private _partner: Partner;
  private _loggedUser: string;
  private _countryCode: string;

  private readonly relativeRelationships : Scope[] = [
    'schools', 'partners'
  ];

  public get auth(): AuthResponse {
    try {
      if (!this._auth) {
        const jsonData = window.localStorage.getItem(bookrawAuthKey);
        if (jsonData != null) {
          this._auth = JSON.parse(jsonData);
        }
      }
      return this._auth;
    } catch {
      return null;
    }
  }

  get school(): UserSchool {
    try {
      if (!this._school) {
        this._school = JSON.parse(window.localStorage.getItem(bookrawSelectedSchool));
      }

      return this._school;
    } catch {
      return null;
    }
  }

  set school(value: UserSchool) {
    localStorage.setItem(bookrawSelectedSchool, JSON.stringify(value));
    this._school = value;

    // Könyv szűrés ország szerinti featurehez, amikor iskola váltás történik itt beállítjuk a countryCode-t
    // a backend middlewarenek (ha nincs iskola akkor marad az IP alapú countryCode és aszerinti szűrés)
    if (value.countryRelation?.alpha2code) this.countryCode = value.countryRelation.alpha2code

    // Ha plural synces iskolát választunk ki, és egy hétnél régebbii a sync, elindítunk egyet
    if ( value.ssoSetting &&
      value.ssoSetting.syncMethod === 'pluralSync' &&
      (!value.lastSsoSync ||  moment( value.lastSsoSync.startedAt ).isBefore(moment().subtract('7', 'days')) )
    ) {
      this.ssoService.sync(this.user.ssoId, value.ssoSchoolId).subscribe()
    }

    this.schoolChange.next(value);
  }

  get partner(): Partner {
    try {
      if (!this._partner) {
        this._partner = JSON.parse(window.localStorage.getItem(bookrawSelectedPartner));
      }
      return this._partner;
    } catch {
      return null;
    }
  }

  set partner(value: Partner) {
    // Könyv szűrés ország szerinti featurehez, amikor iskola váltás történik itt beállítjuk a countryCode-t
    // a backend middlewarenek (ha nincs iskola akkor marad az IP alapú countryCode és aszerinti szűrés)
    if (value.countryRelation?.alpha2code) this.countryCode = value.countryRelation.alpha2code

    localStorage.setItem(bookrawSelectedPartner, JSON.stringify(value));
    this._partner = value;
    this.partnerChange.next(value);
  }

  /**
   * Az ügyfél kérésére csak a Plural partnernél és a Plural SSO-s tanároknál (meg az adminoknál)
   * jelenítjük meg a special mezőt a könyveknél.
   */
  get hasSpecialBooks() {
    return this.partner?.alias === 'plural-partner-LN0Mfm7b'
      || this.school?.ssoSetting?.type === Type.PLURAL
      || (!this.partner && this.user.type === UserType.ADMIN);
  }

  get loggedUser(): string {
    try {
      if (!this._loggedUser) {
        const jsonData = window.localStorage.getItem(bookrawLoggedUser);
        if (jsonData != null) {
          this._loggedUser = jsonData;
        }
      }
      return this._loggedUser;
    } catch {
      return null;
    }
  }

  set loggedUser(value: string) {
    localStorage.setItem(bookrawLoggedUser, value);
    this._loggedUser = value;
    this.loggedUserChange.next(value);
  }

  set countryCode(value: string) {
    localStorage.setItem(bookrawCountryCode, JSON.stringify(value));
    this._countryCode = value;
    this.countryCodeChange.next(value);
  }

  get countryCode(): string {
    if (!this._countryCode) {
      this._countryCode = JSON.parse(window.localStorage.getItem(bookrawCountryCode));
    }
    return this._countryCode;
  }

  get partnerLogo(): string {
    if ( this.partner ) return this.partner?.logoUrls?.original;
    else if (this.school?.partner ) return this.school.partner?.logoUrls?.original;
    else return null;
  }

  public get user(): AuthResponseUser {
    return this.auth?.user;
  }

  public get tokenType(): string {
    return this.auth?.token_type;
  }

  public get token(): string {
    return this.auth?.access_token;
  }

  public get refreshToken(): string {
    return this.auth?.refresh_token;
  }

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private ssoService: SsoSettingsService,
    private router: Router
  ) {}

  private normalizeParams(params: string[]): string[] {
    return params.map((p) => p.toLowerCase().trim());
  }

  public can(
    action: AvailableActions,
    menuParams?: string | string[],
    // fixme szerintem ez egy az egyben eltavolithato, a kodban, csak false beallitast latok
    onlyAdmin: boolean = false,
    prohibitedScopes?: Scope[],
    media?: string | string[],
    elementParams?: string | string[]
  ): boolean {
    // ha nincs 'can' adat megadva akkor mindenhez van joga
    if (!this.user.can || !Object.keys(this.user.can).length) {
      return true;
    }

    // todo kellene erre is egy scope=>getter map a plural miatt
    const relationshipEntity = [];
    relationshipEntity['partners'] = this.partner?.id.toString() || null;
    relationshipEntity['schools'] = this.school?.id.toString() || null;

    // azok a bizonyos "dasbhoard"-ok
    const scope: Scope =
      'schools' in this.user.can ? 'schools' : (
      'partners' in this.user.can ? 'partners' : 'global'
    ),
    // Bizonyos dashboardoknak van külön limitálása, pl a schoolsnál az ssoTeachernek
    limitScope: Scope =
      'whiteLabelTeacher' in this.user.can ? 'whiteLabelTeacher' : (
      'ssoPartner' in this.user.can ? 'ssoPartner' : (
      'ssoTeacher' in this.user.can ? 'ssoTeacher' : (
      'schools' in this.user.can ? 'schools' : (
      'partners' in this.user.can ? 'partners' : 'global'
    ) ) ) ),
    // miket szabad a backend szerint
    rolePermissions: Object = this.relativeRelationships.includes( scope )
    ? this.user.can[ scope ][ relationshipEntity[ scope ] ]
    : this.user.can[ scope ],
    // ezt akarom csinalni
    permission = rolePermissions ? rolePermissions[ action ] : null;

    let hasPermission = <boolean><unknown>permission;
    // az akcio nem szerepel a szerepkor jogosultsagok kozott, vagy
    // az akcio expliciten tiltott a kliensoldalon az adott scopeban
    if ( ! hasPermission || prohibitedScopes?.includes( scope ) || prohibitedScopes?.includes( limitScope ) ) return false;

    // fixme elfaradtam, innen lefele mar refaktoralja mas
    const userTypes = permission.type;
    const userFields = permission['!fields'];

    // fixme typeof ellenorzes redundans lett
    if (menuParams && typeof permission === 'object') {
      // fixme ezt nevezzuk at. ez nem param, hanem h milyen entitas altipus felett van jogod az adott akcioval
      const requiredParams = this.normalizeParams(isString(menuParams) ? [menuParams] : menuParams);

      let containsRequiredParam = false;
      if (userTypes) {
        for (const userType of userTypes) {
          if (requiredParams.includes(userType)) {
            containsRequiredParam = true;
          }
        }
      } else {
        containsRequiredParam = true;
      }

      // fixme onlyadmint migralni bence except megoldasara
      hasPermission = hasPermission && containsRequiredParam && (onlyAdmin === (scope === 'global') );
    }

    // fixme typeof ellenorzes redundans lett
    if (media && typeof permission === 'object') {
      const mediaParams = this.normalizeParams(isString(media) ? [media] : media);
      let containsMediaParam = false;

      if (userFields) {
        for (const userField of userFields) {
          if (mediaParams.includes(userField)) {
            containsMediaParam = true;
          }
        }
      }
      hasPermission = hasPermission && !containsMediaParam;
    }

    // fixme typeof ellenorzes redundans lett
    if (!menuParams && onlyAdmin && elementParams && typeof permission === 'object') {
      const elements = this.normalizeParams(isString(elementParams) ? [elementParams] : elementParams);

      let containsElementParam = false;
      if (userTypes) {
        for (const userField of userTypes) {
          if (elements.includes(userField)) {
            containsElementParam = true;
          }
        }
      }

      hasPermission = hasPermission && !containsElementParam && scope !== 'global';
    }

    return hasPermission;
  }

  login(loginData: { username: string, password: string }, type: 'password'): Observable<any>
  login(loginData: { idToken: string }, type: 'google'): Observable<any>
  login(loginData: { idToken: string, nonApple: boolean }, type: 'apple'): Observable<any>
  login(loginData, type): Observable<any> {
    this.http.get('https://ipapi.co/json/').subscribe(res => this.countryCode = res['country'])

    const data = Object.assign({
      'client_id': environment.clientId,
      'client_secret': environment.clientSecret,
    }, loginData);
    return this.http.post<AuthResponse>(environment.baseURL + '/api/oauth/token/' + type, data).pipe(tap((res) => {
      this.saveData(res);
      this.userLogin.next(res.user);
      const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
      this.router.navigateByUrl(returnUrl);
    }));
  }

  loginWithSso(ssoId: string, loginData: { code: string, state?: string }): Observable<any> {
    const data = Object.assign({
      'client_id': environment.clientId,
      'client_secret': environment.clientSecret,
      // mivel tobb domainrol is bejon az admin, ugyan arra a domainre iranyitunk vissza, ahonnan jott
      'redirectUri': location.protocol + '//' + location.host,
    }, loginData);
    return this.http.get<AuthResponse>(environment.baseURL + '/api/oauth/sso/admin/signin/' + ssoId, {
      params: data
    }).pipe(tap((res) => {
      this.saveData(res);
      this.userLogin.next(res.user);
      const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
      this.router.navigateByUrl(returnUrl);
    }));
  }

  loginWithGoogle(loginData: { authCode: string }): Observable<any> {
    const data = Object.assign({
      'client_id': environment.clientId,
      'client_secret': environment.clientSecret,
      // mivel tobb domainrol is bejon az admin, ugyan arra a domainre iranyitunk vissza, ahonnan jott
      'redirectUri': location.protocol + '//' + location.host,
    }, loginData);
    return this.http.post<AuthResponse>(environment.baseURL + '/api/oauth/token/google', data).pipe(tap((res) => {
      this.saveData(res);
      this.userLogin.next(res.user);
      const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
      this.router.navigateByUrl(returnUrl);
    }));
  }

  logout() {
    window.localStorage.removeItem(bookrawAuthKey);
    window.localStorage.removeItem(bookrawSelectedSchool);
    window.localStorage.removeItem(bookrawSelectedPartner);
    window.localStorage.removeItem(bookrawLoggedUser);
    window.localStorage.removeItem(bookrawCountryCode);
    this._auth = null;
    this._school = null;
    this._partner = null;
    this._loggedUser = null;
    this._countryCode = null;

    this.router.navigateByUrl('/sessions/signin');
    return this.http.delete(environment.baseURL + '/api/oauth/token');
  }

  saveData(data: AuthResponse) {
    window.localStorage.setItem(bookrawAuthKey, JSON.stringify(data));
    window.localStorage.setItem(bookrawLoggedUser, data.user.name);
    this.loginChanges.next(data.user);
    this.loggedUserChange.next(data.user.name);
  }

  /**
   * A bejelentkezett felhasználóról tárolt adatok frissítése.
   * Óránként csak egyszer frissít.
   *
   * @param usersService
   * @param force
   */
  updateMe(usersService: UsersService, force: boolean = false) {

    if (!force && (!this._auth?.user || !moment.unix(this.auth.user.lastUpdate ?? 0).isBefore(moment().subtract(1, 'h')))) {
      return;
    }
    usersService.getMe().subscribe(response => {
      this.saveData(Object.assign(this.auth, {user: {...response.result, can: this.auth.user.can, lastUpdate: moment().unix()}}));
      this._auth = null; // Újra betölti local storage-ből
      const pid = this.partner?.id;
      const sid = this.school?.id;
      if (pid) {
        this.partner = this.user.partners.find(partner => partner.id === pid);
      }
      if (sid) {
        this.school = this.user.schools.find(school => school.id === sid);
      }
    });
  }
}
